import { Client, Conversation } from "@twilio/conversations";
import { useLoading } from "providers/loading";
import { useNotify } from "providers/notify";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  onOpenFarmChatContainer,
  onReloadFarmHeaderChat as onReloadHeaderChatStore,
  setFarmChatList,
  setFarmTwlIdentify,
  setFarmTwlToken,
} from "redux/actions/farmChat";
import {
  getFarmChatClient,
  getFarmTwlIdentity,
} from "redux/selectors/farmChat";
import { apiGetIdentity, apiGetFarmIdentifyProfile, apiUnlockConversation } from "services/api/chat";
import { concatIdentity, isNotNull } from "helpers/string";
import useUser from "./useUser";
import useAuth from "./useAuth";
import { LOCALSTORAGE_KEYS, chatStorage } from "helpers/localStorage";

type CreateConvsData = {
  identity: string;
};

const useFarmChat = () => {
  const chatClient: Client = useSelector(getFarmChatClient);
  const twlIdentify = useSelector(getFarmTwlIdentity);
  const { setLoading } = useLoading();
  const { setNotify } = useNotify();
  const dispatch = useDispatch();
  const { isLoggedIn } = useUser();
  const { onDirectLogin } = useAuth();

  const t = useIntl();

  const onOpenConversationWithIdentity = async (identity: string) => {
    console.log('useFarmChat - onOpenConversationWithIdentity called:', { 
      identity, 
      isLoggedIn, 
      twlIdentify, 
      chatClient: !!chatClient 
    });
    
    if (isLoggedIn) {
      if (!chatClient) {
        console.error('Farm chat client is not initialized');
        return;
      }
      
      const conversationName = concatIdentity(identity, twlIdentify as string);
      console.log('Conversation name:', conversationName);
      
      // Also try to find conversation with reverse order (regular chat format)
      const reverseConversationName = `${twlIdentify}|${identity}`;
      console.log('Reverse conversation name:', reverseConversationName);
      
      const conversationExist = await checkConversationExist(conversationName);
      console.log('Conversation exists:', !!conversationExist);
      
      // If not found, try reverse format
      let reverseConversationExist;
      if (!conversationExist) {
        reverseConversationExist = await checkConversationExist(reverseConversationName);
        console.log('Reverse conversation exists:', !!reverseConversationExist);
      }
      
      const tmpConversation = {
        uniqueName: conversationName,
        attributes: { isFarmChat: true },
      } as unknown as Conversation;
      
      const existingConv = conversationExist || reverseConversationExist;
      if (existingConv) {
        console.log('Opening existing conversation:', existingConv.sid);
        onOpenConversation(existingConv.sid);
      } else {
        console.log('Creating new temporary conversation');
        dispatch(onOpenFarmChatContainer(tmpConversation));
      }
    } else {
      console.log('User not logged in, redirecting to login');
      onDirectLogin();
    }
  };

  const checkConversationExist = async (
    value: string,
    type: keyof Conversation = "uniqueName"
  ): Promise<Conversation | undefined> => {
    try {
      let paginator = await chatClient.getSubscribedConversations();
      let conversations = paginator.items;
      while (paginator.hasNextPage) {
        paginator = await paginator.nextPage();
        conversations = conversations.concat(paginator.items);
      }
      
      // Debug: log all conversation unique names
      console.log('All farm conversations:', conversations.map(c => c.uniqueName));
      console.log('Looking for conversation:', value);
      
      const conversationExist = conversations.some(
        (conversation) => conversation[type] === value
      );
      return conversationExist
        ? conversations.find((conversation) => conversation[type] === value)
        : undefined;
    } catch (error) {
      console.error("Error checking conversation:", error);
      return undefined;
    }
  };

  const onCreateConverstation = async (
    data: CreateConvsData
  ): Promise<Conversation | undefined> => {
    const conversationName = concatIdentity(
      data.identity,
      twlIdentify as string
    );
    try {
      if (chatClient) {
        setLoading(true);
        const { identity } = data;
        const conversation = await chatClient.createConversation({
          friendlyName: identity,
          uniqueName: conversationName,
          attributes: { isFarmChat: true },
        });
        await conversation.join();
        const supplierIdentityData = await apiGetIdentity(identity);
        const supplierIdentity = supplierIdentityData.data?.message?.data;
        await conversation.add(supplierIdentity);
        onReloadHeaderChat();
        dispatch(onOpenFarmChatContainer(conversation));
        setLoading(false);
        return conversation;
      }
    } catch (error: any) {
      console.log(error?.message);
      setLoading(false);
      setNotify({
        open: true,
        type: "error",
        msg: t.formatMessage({ id: "something_went_wrong" }),
      });
    }
  };

  const onDeleteConversation = useCallback(async (sid: string) => {
    if (chatClient) {
      try {
        const conversation = await chatClient.getConversationBySid(sid);
        await conversation.delete();
      } catch (error) {
        console.error(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getConversationById = async (conversationId: string) => {
    try {
      const conversation = await chatClient.getConversationBySid(
        conversationId
      );
      return conversation;
    } catch (error) {
      console.error("Error fetching conversation:", error);
      throw error;
    }
  };

  const getTotalUnreadMessagesCount = async () => {
    try {
      const paginator = await chatClient.getSubscribedConversations();
      const conversations = paginator.items;
      let unreadCounts = 0;
      for (const conversation of conversations) {
        const unreadCount = await getUnreadCountForConversation(conversation);
        unreadCounts += unreadCount;
      }
      return unreadCounts;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      return 0;
    }
  };

  const getUnreadCountForConversation = async (
    conversation: Conversation
  ): Promise<number> => {
    try {
      const messages = await conversation.getMessages();
      const messagesFromOther = messages.items.filter(
        (message) => message.author !== twlIdentify
      );
      const lastMessageIndex =
        messagesFromOther[messagesFromOther.length - 1]?.index;
      let messagesCount = 0;
      const participants = await conversation.getParticipants();
      const myParticipant = participants.find(
        (participant) => participant.identity === twlIdentify
      );
      const myLastReadMessageIndex = myParticipant?.lastReadMessageIndex || 0;
      if (!isNotNull(myLastReadMessageIndex)) {
        messagesCount = messagesFromOther.length;
      } else {
        if (isNotNull(lastMessageIndex) && lastMessageIndex > myLastReadMessageIndex) {
          messagesCount = lastMessageIndex - myLastReadMessageIndex;
        }
      }
      return messagesCount;
    } catch (error) {
      console.error(
        `Error getting unread count for conversation ${conversation.sid}:`,
        error
      );
      return 0;
    }
  };

  const markAllMessagesAsRead = async (conversation: Conversation) => {
    try {
      // Get messages and find the last message index
      const messages = await conversation.getMessages();
      if (messages.items.length > 0) {
        const lastMessage = messages.items[messages.items.length - 1];
        await conversation.updateLastReadMessageIndex(lastMessage.index);
        // Update the store to reflect changes
        dispatch(onReloadHeaderChatStore());
      }
    } catch (error) {
      console.error(
        `Error marking messages as read in conversation ${conversation.sid}:`,
        error
      );
    }
  };

  const onOpenConversation = async (conversationSid: string) => {
    try {
      const conversation = await chatClient.getConversationBySid(
        conversationSid
      );
      dispatch(onOpenFarmChatContainer(conversation));
    } catch (error) {
      console.error("Error opening conversation:", error);
    }
  };

  const sendAndMarkReadMessage = async (
    conversation: Conversation,
    message: string
  ) => {
    await conversation.sendMessage(message);
  };

  const onClickSendMessage = async (message: string, identity: string) => {
    if (identity && message.trim() !== "") {
      try {
        const conversations = await chatClient.getSubscribedConversations();
        const conversation = conversations.items.find(async (conv) => {
          const participant = await conv.getParticipantByIdentity(identity);
          return participant ? true : false;
        });
        if (!conversation) {
          const newConversation: Conversation | undefined =
            await onCreateConverstation({
              identity,
            });
          if (newConversation) {
            sendAndMarkReadMessage(newConversation, message);
          }
        } else {
          sendAndMarkReadMessage(conversation, message);
        }
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  };

  const onReloadHeaderChat = () => {
    dispatch(onReloadHeaderChatStore());
  };

  const setStoreTwl = () => {
    dispatch(setFarmTwlToken());
    dispatch(setFarmTwlIdentify());
  };

  const setLocalStorerageTwl = () => {
    chatStorage.remove(LOCALSTORAGE_KEYS.farmTwlk);
    chatStorage.remove(LOCALSTORAGE_KEYS.farmTwlid);
  };

  const onClearChatKey = () => {
    dispatch(setFarmChatList([]));
    setStoreTwl();
    setLocalStorerageTwl();
  };

  const onFetchMessages = async (conversation: Conversation) => {
    try {
      if (conversation.sid) {
        const paginator = await conversation.getMessages();
        return paginator.items.filter((message) => {
          const messageAttributes: any = message.attributes;
          const hideIdentityList: string[] = messageAttributes?.hidden || [];
          return hideIdentityList.indexOf(twlIdentify) < 0;
        });
      }
      return [];
    } catch (error) {
      console.error("Error fetching messages:", error);
      return [];
    }
  };

  const onFetchParticipants = async (conversation: Conversation) => {
    try {
      return await conversation.getParticipants();
    } catch (error) {
      console.error("Error fetching participants:", error);
      return [];
    }
  };

  const onFetchCustomerDetail = async (customerIdentity: string) => {
    try {
      const response = await apiGetFarmIdentifyProfile(customerIdentity);
      return response?.data?.profile || {};
    } catch (error) {
      console.error("Error fetching customer detail:", error);
      return {};
    }
  };

  const onUnlockChat = async (conversationSid: string) => {
    try {
      await apiUnlockConversation(conversationSid);
      dispatch(onReloadHeaderChatStore());
      return true;
    } catch (error) {
      console.error("Error unlocking chat:", error);
      return false;
    }
  };

  return {
    chatClient,
    twlIdentify,
    onCreateConverstation,
    onDeleteConversation,
    onOpenConversationWithIdentity,
    getConversationById,
    getTotalUnreadMessagesCount,
    onOpenConversation,
    markAllMessagesAsRead,
    getUnreadCountForConversation,
    onClickSendMessage,
    onReloadHeaderChat,
    onClearChatKey,
    onFetchMessages,
    onFetchParticipants,
    onFetchCustomerDetail,
    onUnlockChat,
  };
};
export default useFarmChat;
